import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Users, Lock, ChevronRight, MapPin, Briefcase } from 'lucide-react';
import { usePageHeader } from '../context/PageHeaderContext';

const MyTeamPage = () => {
  const navigate = useNavigate();
  const { api, user, isFeatureEnabled } = useAuth();
  usePageHeader({ title: 'My Team' }, []);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('onboarded');
  const [roleFilter, setRoleFilter] = useState('all');

  const isOnboarded = user?.is_onboarded || user?.role === 'admin';
  const currentRole = user?.role;

  useEffect(() => {
    if (isOnboarded) {
      loadUsers();
    } else {
      setLoading(false);
    }
  }, [isOnboarded]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users/team');
      setAllUsers(res.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (u) => {
    if (u?.first_name && u?.last_name) {
      return `${u.first_name[0]}${u.last_name[0]}`.toUpperCase();
    }
    if (u?.full_name) {
      return u.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  const getFullName = (u) => {
    if (u?.first_name && u?.last_name) {
      return `${u.first_name} ${u.last_name}`;
    }
    return u?.full_name || 'Unknown User';
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin': return 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20';
      case 'supervisor': return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
      case 'lead': return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20';
      default: return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20';
    }
  };

  const getRoleGradient = (role) => {
    switch (role) {
      case 'admin': return 'from-rose-500 to-pink-600';
      case 'supervisor': return 'from-blue-500 to-indigo-600';
      case 'lead': return 'from-emerald-500 to-teal-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  // Get filtered users based on current user's role, active tab, and role filter
  const getFilteredUsers = () => {
    let filtered = [...allUsers];
    const currentState = user?.state;
    const currentIndustry = user?.industry;

    // Step 1: Filter by tab (onboarded, pending, rejected)
    if (activeTab === 'onboarded') {
      filtered = filtered.filter(u => u.is_onboarded);
    } else if (activeTab === 'pending') {
      filtered = filtered.filter(u => !u.is_onboarded && !u.step2d_interview_rejected && !u.step2e_interview_rejected);
    } else if (activeTab === 'rejected') {
      filtered = filtered.filter(u => u.step2d_interview_rejected || u.step2e_interview_rejected);
    }

    // Step 2: Filter by current user's role hierarchy
    if (currentRole === 'admin') {
      // Admin sees supervisors, leads, and affiliates (not other admins - those go in My Peers)
      filtered = filtered.filter(u => u.role !== 'admin');
    } else if (currentRole === 'supervisor') {
      // Supervisor sees leads and affiliates in their state only
      filtered = filtered.filter(u => 
        (u.role === 'lead' || u.role === 'affiliate') && 
        u.state === currentState
      );
    } else if (currentRole === 'lead') {
      // Lead sees only affiliates in their state AND industry
      filtered = filtered.filter(u => 
        u.role === 'affiliate' && 
        u.state === currentState && 
        u.industry === currentIndustry
      );
    }

    // Step 3: Apply role filter (All, Supervisors, Leads, Affiliates)
    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }

    return filtered;
  };

  // Get available role filters based on current user's role
  const getRoleFilters = () => {
    if (currentRole === 'admin') {
      return ['all', 'supervisor', 'lead', 'affiliate'];
    } else if (currentRole === 'supervisor') {
      return ['all', 'lead', 'affiliate'];
    }
    // Lead doesn't need filters (only sees affiliates)
    return [];
  };

  const roleFilters = getRoleFilters();
  const filteredUsers = getFilteredUsers();

  // Affiliates don't have My Team page
  if (currentRole === 'affiliate') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="my-team-page">
        <div className="max-w-2xl mx-auto pt-4">
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-slate-400" />
            </div>
            <h2 className="text-xl font-bold mb-2">My Team</h2>
            <p className="text-muted-foreground text-center max-w-sm">
              As an affiliate, you don't have team management access. Check out "My Peers" to connect with other affiliates in your area.
            </p>
            <Button className="mt-4" onClick={() => navigate('/my-peers')}>
              View My Peers
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Non-onboarded blocker
  if (!isOnboarded) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="my-team-page">
        <div className="max-w-2xl mx-auto pt-4 relative">
          <div className="absolute inset-0 backdrop-blur-md bg-white/50 dark:bg-black/50 rounded-xl z-10 flex flex-col items-center justify-center min-h-[400px]">
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Complete Onboarding</h2>
            <p className="text-muted-foreground text-center max-w-sm mb-4">
              You need to complete your onboarding process before accessing your team.
            </p>
            <Button onClick={() => navigate('/')}>
              Return to Onboarding
            </Button>
          </div>
          
          <div className="opacity-30 pointer-events-none">
            <h1 className="text-2xl font-bold mb-4">My Team</h1>
            <Card className="h-64 bg-white/50 dark:bg-[#141414]/50" />
          </div>
        </div>
      </div>
    );
  }

  // User Card Component - Individual container boxes like Recent Calls
  const UserCard = ({ u }) => (
    <Card 
      className="bg-white/90 dark:bg-[#141414] border-0 shadow-md hover:shadow-lg transition-all cursor-pointer"
      onClick={() => navigate(`/profile/${u.id}?from=team`)}
      data-testid={`team-member-${u.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Avatar className="h-12 w-12 border-2 border-white dark:border-[#1a1a1a] shadow-md">
            <AvatarImage src={u.profile_picture} />
            <AvatarFallback className={`text-sm font-bold bg-gradient-to-br ${getRoleGradient(u.role)} text-white`}>
              {getInitials(u)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800 dark:text-white truncate">
              {getFullName(u)}
            </p>
            <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
              {u.state && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {u.state}
                </span>
              )}
              {u.industry && (
                <span className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  {u.industry}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`text-xs capitalize px-2.5 py-1 border ${getRoleBadgeStyle(u.role)}`}>
              {u.role}
            </Badge>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="my-team-page">
      <div className="max-w-2xl mx-auto pt-4">
        {/* Header */}
        {/* Three-Prong Slider Tabs */}
        <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg mb-4">
          <CardContent className="p-2">
            <div className="flex rounded-xl bg-[#f5f5f5] dark:bg-[#0d0d0d] p-1">
              {isFeatureEnabled('my_team_onboarded') && (
                <button
                  key="onboarded"
                  onClick={() => setActiveTab('onboarded')}
                  className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all capitalize ${
                    activeTab === 'onboarded'
                      ? 'bg-white dark:bg-[#1a1a1a] text-[#1c1a1c] dark:text-white shadow-sm'
                      : 'text-[#676767] hover:text-[#1c1a1c] dark:hover:text-white'
                  }`}
                  data-testid="tab-onboarded"
                >
                  onboarded
                </button>
              )}
              {isFeatureEnabled('my_team_pending') && (
                <button
                  key="pending"
                  onClick={() => setActiveTab('pending')}
                  className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all capitalize ${
                    activeTab === 'pending'
                      ? 'bg-white dark:bg-[#1a1a1a] text-[#1c1a1c] dark:text-white shadow-sm'
                      : 'text-[#676767] hover:text-[#1c1a1c] dark:hover:text-white'
                  }`}
                  data-testid="tab-pending"
                >
                  pending
                </button>
              )}
              {isFeatureEnabled('my_team_rejected') && (
                <button
                  key="rejected"
                  onClick={() => setActiveTab('rejected')}
                  className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all capitalize ${
                    activeTab === 'rejected'
                      ? 'bg-white dark:bg-[#1a1a1a] text-[#1c1a1c] dark:text-white shadow-sm'
                      : 'text-[#676767] hover:text-[#1c1a1c] dark:hover:text-white'
                  }`}
                  data-testid="tab-rejected"
                >
                  rejected
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Role Filter Buttons - Match Call History filter style */}
        {roleFilters.length > 0 && (
          <div className="flex gap-1.5 mb-4">
            {roleFilters.map((filter) => (
              <Button
                key={filter}
                variant={roleFilter === filter ? 'default' : 'outline'}
                size="sm"
                className={`flex-1 h-8 text-xs capitalize ${
                  roleFilter === filter ? 'bg-[#2C3E50] hover:bg-[#34495E]' : ''
                }`}
                onClick={() => setRoleFilter(filter)}
                data-testid={`filter-${filter}`}
              >
                {filter === 'all' ? 'All' : `${filter}s`}
              </Button>
            ))}
          </div>
        )}

        {/* User List - Individual Container Boxes */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin mb-3" />
            <span className="text-slate-500 text-sm">Loading team members...</span>
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-1">No team members found</h3>
              <p className="text-sm text-slate-500">
                {activeTab === 'onboarded' && 'No onboarded team members yet.'}
                {activeTab === 'pending' && 'No pending team members.'}
                {activeTab === 'rejected' && 'No rejected team members.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-[#676767] dark:text-gray-400 px-1">
              {filteredUsers.length} {filteredUsers.length === 1 ? 'member' : 'members'}
            </p>
            {filteredUsers.map((u) => (
              <UserCard key={u.id} u={u} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTeamPage;
